'use client';
import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import styles from './FormSelect.module.css';

export interface FormSelectOption {
	value: string;
	label: string;
	action?: boolean;
}

interface BaseFormSelectProps {
	options: FormSelectOption[];
	placeholder?: string;
	hasError?: boolean;
	disabled?: boolean;
	searchable?: boolean;
	clearable?: boolean;
}

interface SingleFormSelectProps extends BaseFormSelectProps {
	value: string;
	onChange: (value: string) => void;
	multiple?: false;
}

interface MultipleFormSelectProps extends BaseFormSelectProps {
	value: string[];
	onChange: (value: string[]) => void;
	multiple: true;
}

export type FormSelectProps = SingleFormSelectProps | MultipleFormSelectProps;

export const FormSelect = ({
	value,
	onChange,
	options,
	placeholder = 'Selecione',
	hasError,
	disabled,
	searchable,
	clearable,
	multiple,
}: FormSelectProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [search, setSearch] = useState('');
	const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
	const wrapperRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const searchRef = useRef<HTMLInputElement>(null);

	const updatePosition = useCallback(() => {
		if (!triggerRef.current) return;
		const rect = triggerRef.current.getBoundingClientRect();
		setDropdownStyle({
			top: rect.bottom + 4,
			left: rect.left,
			width: rect.width,
		});
	}, []);

	useEffect(() => {
		const handleOutsideClick = (e: MouseEvent) => {
			const target = e.target as Node;
			if (
				wrapperRef.current &&
				!wrapperRef.current.contains(target) &&
				dropdownRef.current &&
				!dropdownRef.current.contains(target)
			) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			updatePosition();
			document.addEventListener('mousedown', handleOutsideClick);
			document.addEventListener('scroll', updatePosition, true);
			window.addEventListener('resize', updatePosition);
			if (searchable) {
				setTimeout(() => searchRef.current?.focus(), 0);
			}
		} else {
			setSearch('');
		}

		return () => {
			document.removeEventListener('mousedown', handleOutsideClick);
			document.removeEventListener('scroll', updatePosition, true);
			window.removeEventListener('resize', updatePosition);
		};
	}, [isOpen, searchable, updatePosition]);

	const selectedValues = multiple ? value : value ? [value] : [];
	const selected = !multiple ? options.find(o => o.value === value) : undefined;
	const selectedOptions = multiple ? options.filter(o => value.includes(o.value)) : [];
	const hasValue = multiple ? value.length > 0 : !!value;
	const triggerText = multiple
		? selectedOptions.length > 0
			? selectedOptions.map(o => o.label).join(', ')
			: placeholder
		: selected?.label ?? placeholder;

	const visibleOptions =
		searchable && search
			? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
			: options;

	const handleSelect = (option: FormSelectOption) => {
		if (multiple) {
			const next = value.includes(option.value)
				? value.filter(v => v !== option.value)
				: [...value, option.value];
			onChange(next);
			if (option.action) setIsOpen(false);
			return;
		}
		onChange(option.value);
		setIsOpen(false);
	};

	const handleClear = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (multiple) onChange([]);
		else onChange('');
		setIsOpen(false);
	};

	const dropdown = isOpen ? (
		<div ref={dropdownRef} className={styles.dropdown} style={dropdownStyle}>
			{searchable && (
				<div className={styles.searchWrapper}>
					<SearchOutlined className={styles.searchIcon} />
					<input
						ref={searchRef}
						type="text"
						className={styles.searchInput}
						value={search}
						onChange={e => setSearch(e.target.value)}
						placeholder="Pesquisar..."
						onMouseDown={e => e.stopPropagation()}
					/>
				</div>
			)}
			<div className={styles.optionsList}>
				{visibleOptions.length === 0 ? (
					<div className={styles.noResults}>Nenhum resultado</div>
				) : (
					visibleOptions.map(option => (
						<div
							key={option.value}
							className={`${styles.option} ${selectedValues.includes(option.value) ? styles.optionSelected : ''} ${option.action ? styles.optionAction : ''}`}
							onMouseDown={() => handleSelect(option)}
						>
							{multiple && !option.action && (
								<span
									className={`${styles.checkbox} ${selectedValues.includes(option.value) ? styles.checkboxChecked : ''}`}
								/>
							)}
							{option.label}
						</div>
					))
				)}
			</div>
		</div>
	) : null;

	return (
		<div ref={wrapperRef} className={styles.wrapper}>
			<button
				ref={triggerRef}
				type="button"
				disabled={disabled}
				onClick={() => !disabled && setIsOpen(o => !o)}
				className={`${styles.trigger} ${!hasValue ? styles.empty : ''} ${hasError ? styles.error : ''} ${isOpen ? styles.open : ''}`}
			>
				<span className={styles.triggerText}>{triggerText}</span>
				{clearable && hasValue && !disabled && (
					<span
						role="button"
						aria-label="Limpar seleção"
						className={styles.clearButton}
						onMouseDown={e => e.stopPropagation()}
						onClick={handleClear}
					>
						<CloseOutlined className={styles.clearIcon} />
					</span>
				)}
				<ExpandMoreOutlined
					className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
				/>
			</button>
			{typeof document !== 'undefined' && createPortal(dropdown, document.body)}
		</div>
	);
};
